import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const FIELD = "w-full bg-transparent border border-aura-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";

export function FaqManager() {
  const [faqs, setFaqs] = useState([]);
  const [nq, setNq] = useState("");
  const [na, setNa] = useState("");
  const [adding, setAdding] = useState(false);
  const { t } = useLang();

  const load = () => api.get("/faqs").then(({ data }) => setFaqs(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nq.trim() || !na.trim()) { toast.error(t("faqm.fill")); return; }
    setAdding(true);
    try { await api.post("/admin/faqs", { question: nq, answer: na, order: faqs.length }); setNq(""); setNa(""); await load(); toast.success(t("faqm.added")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setAdding(false); }
  };

  const saveOne = async (f) => {
    try { await api.patch(`/admin/faqs/${f.id}`, { question: f.question, answer: f.answer, order: f.order }); toast.success(t("faqm.updated")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/faqs/${id}`); await load(); toast.success(t("faqm.deleted")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const edit = (id, key, val) => setFaqs((arr) => arr.map((f) => (f.id === id ? { ...f, [key]: val } : f)));

  return (
    <div data-testid="faq-manager" className="max-w-3xl">
      <div className="rounded-xl border border-aura-border bg-aura-surface p-5 mb-6">
        <h3 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2"><Plus size={18} /> {t("faqm.newTitle")}</h3>
        <input data-testid="faq-new-question" className={`${FIELD} mb-3`} placeholder={t("faqm.question")} value={nq} onChange={(e) => setNq(e.target.value)} />
        <textarea data-testid="faq-new-answer" rows={2} className={`${FIELD} mb-3`} placeholder={t("faqm.answer")} value={na} onChange={(e) => setNa(e.target.value)} />
        <button data-testid="faq-add-btn" onClick={add} disabled={adding} className="rounded-full bg-white text-black text-sm font-semibold px-5 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-60 flex items-center gap-2">
          {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t("faqm.add")}
        </button>
      </div>

      <div className="space-y-4">
        {faqs.map((f) => (
          <div key={f.id} className="rounded-xl border border-aura-border bg-aura-surface p-5">
            <input className={`${FIELD} mb-2 font-medium`} value={f.question} onChange={(e) => edit(f.id, "question", e.target.value)} />
            <textarea rows={3} className={FIELD} value={f.answer} onChange={(e) => edit(f.id, "answer", e.target.value)} />
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => saveOne(f)} className="rounded-lg border border-aura-border hover:border-white/40 text-sm text-zinc-200 px-4 py-2 flex items-center gap-2 transition-colors"><Save size={14} /> {t("faqm.save")}</button>
              <button onClick={() => del(f.id)} className="rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
